import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImages, faPlus, faSearch, faFolder, faChevronDown, faChevronUp, faCheck, faArrowLeft, faTrash, faThumbtack } from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import { getFolderDisplayLabel } from '../utils/folderHierarchy';
import { useAccount } from '../contexts/AccountContext';
import { getUserData } from '../utils/userUtils';
import { useAccountSettings } from '../hooks/useAccountSettings';
import { isUploadCreativesHidden } from '../utils/uploadCreativesGating';
import DataTable, { createActionColumn } from './DataTable';
import StatusBadge from './StatusBadge';
import './Pages.css';
import './CreativeManagerPage.css';

/**
 * CreativeManagerPage component for managing creative upload jobs
 * Displays all creative manager jobs for the current account
 */
const CreativeManagerPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAccountIdToUse } = useAccount();
  const [user] = useState(() => getUserData());
  const accountId = getAccountIdToUse();
  const { settings: accountSettings } = useAccountSettings(accountId, user);
  const uploadCreativesHidden = isUploadCreativesHidden(accountSettings);

  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Folder filter state
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showFolderDropdown, setShowFolderDropdown] = useState(false);
  const [foldersLoading, setFoldersLoading] = useState(false);

  // Pagination state
  const getPersistedPaginationState = () => {
    const urlFolderId = searchParams.get('folderId');
    try {
      const saved = sessionStorage.getItem('creativeManagerPage_paginationState');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (urlFolderId) parsed.selectedFolderId = urlFolderId;
        return parsed;
      }
    } catch (err) {
      console.error('Error loading pagination state:', err);
    }
    return {
      currentPage: 1,
      itemsPerPage: 10,
      sortBy: 'created_at',
      sortOrder: 'desc',
      searchTerm: '',
      selectedFolderId: urlFolderId || null
    };
  };

  const initialState = getPersistedPaginationState();

  const [currentPage, setCurrentPage] = useState(initialState.currentPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialState.itemsPerPage);
  const [sortBy, setSortBy] = useState(initialState.sortBy);
  const [sortOrder, setSortOrder] = useState(initialState.sortOrder);
  const [searchTerm, setSearchTerm] = useState(initialState.searchTerm);
  const [selectedFolderId, setSelectedFolderId] = useState(initialState.selectedFolderId);
  const [pagination, setPagination] = useState({
    currentPage: initialState.currentPage,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: initialState.itemsPerPage,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Dropdown state
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRefs = useRef({});

  // Persist pagination state
  const persistPaginationState = (state) => {
    try {
      sessionStorage.setItem('creativeManagerPage_paginationState', JSON.stringify(state));
    } catch (err) {
      console.error('Error saving pagination state:', err);
    }
  };

  useEffect(() => {
    persistPaginationState({ currentPage, itemsPerPage, sortBy, sortOrder, searchTerm, selectedFolderId });
  }, [currentPage, itemsPerPage, sortBy, sortOrder, searchTerm, selectedFolderId]);

  // Fetch folders when account changes
  useEffect(() => {
    if (accountId && user) fetchFolders();
  }, [accountId, user]);

  // Fetch jobs when filters change
  useEffect(() => {
    if (accountId && user) fetchJobs();
  }, [accountId, user, currentPage, itemsPerPage, sortBy, sortOrder, searchTerm, selectedFolderId]);

  const fetchJobs = async () => {
    if (!accountId || !user) return;
    try {
      setIsLoading(true);
      setError(null);

      const params = {
        accountId,
        userId: user?.userId || user?.id,
        search: searchTerm,
        sortBy,
        sortOrder,
        page: currentPage,
        limit: itemsPerPage
      };
      if (selectedFolderId) params.folderId = selectedFolderId;

      const response = await api.get(getApiUrl('/api/v2/creative-manager'), { params });
      const jobsData = response.data.jobs || [];

      // Sort with pinned items first
      jobsData.sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return 0;
      });

      setJobs(jobsData);
      if (response.data.pagination) {
        setPagination({
          currentPage: response.data.pagination.page,
          totalPages: response.data.pagination.totalPages,
          totalItems: response.data.pagination.totalItems,
          itemsPerPage: response.data.pagination.limit,
          hasNextPage: response.data.pagination.hasNextPage,
          hasPrevPage: response.data.pagination.hasPrevPage
        });
      }
    } catch (err) {
      console.error('Error fetching creative manager jobs:', err);
      setError(err.response?.data?.error || 'Failed to load creative manager jobs');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFolders = async () => {
    if (!accountId || !user) return;
    try {
      setFoldersLoading(true);
      const response = await api.get(getApiUrl('/api/v2/folders'), {
        params: { accountId, userId: user?.userId || user?.id, limit: 100 }
      });
      const foldersData = response.data.folders || response.data || [];
      setFolders(foldersData);
      if (selectedFolderId && !selectedFolder) {
        const folder = foldersData.find(f => f.id === selectedFolderId);
        if (folder) setSelectedFolder(folder);
        else { setSelectedFolderId(null); setSelectedFolder(null); }
      }
    } catch (err) {
      console.error('Error fetching folders:', err);
    } finally {
      setFoldersLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const requestSort = (key) => {
    let direction = 'desc';
    if (sortBy === key && sortOrder === 'desc') direction = 'asc';
    setSortBy(key);
    setSortOrder(direction);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage);
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleFolderSelect = (folder) => {
    if (folder) {
      setSelectedFolder(folder);
      setSelectedFolderId(folder.id);
      setSearchParams({ folderId: folder.id });
    } else {
      setSelectedFolder(null);
      setSelectedFolderId(null);
      setSearchParams({});
    }
    setCurrentPage(1);
    setShowFolderDropdown(false);
  };

  const toggleDropdown = (jobId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (openDropdownId === jobId) { setOpenDropdownId(null); return; }
    const buttonRect = e.currentTarget.getBoundingClientRect();
    const dropdownWidth = 160;
    const dropdownHeight = 80;
    let top = buttonRect.bottom + 5;
    let left = buttonRect.right - dropdownWidth;
    if (left < 10) left = 10;
    if (top + dropdownHeight > window.innerHeight - 10) top = buttonRect.top - dropdownHeight - 5;
    setDropdownPosition({ top, left });
    setOpenDropdownId(jobId);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !Object.values(dropdownRefs.current).some(ref => ref && ref.contains(event.target))) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownId]);

  // Close folder dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFolderDropdown && !event.target.closest('.folder-filter-container')) {
        setShowFolderDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFolderDropdown]);

  const formatDate = (job) => {
    const date = job.createdAt;
    if (!date) return 'Unknown';
    const jobDate = new Date(date);
    const now = new Date();
    const isToday = jobDate.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = jobDate.toDateString() === yesterday.toDateString();
    if (isToday) return `Today ${jobDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    if (isYesterday) return `Yesterday ${jobDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    const diffTime = Math.abs(now - jobDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays <= 7) return `${diffDays} days ago`;
    return jobDate.toLocaleDateString();
  };

  // Get platform status counts for a job
  const getPlatformSummary = (job) => {
    const ps = job.platformStatus || {};
    
    // Helper to extract status (handles both string and object formats)
    const getStatus = (platformData) => {
      if (typeof platformData === 'string') return platformData;
      if (typeof platformData === 'object' && platformData?.status) return platformData.status;
      return 'skipped'; // Default to skipped if no status found
    };
    
    // Always show all 6 platforms with their respective statuses
    const platforms = [
      { name: 'Google Ads', status: getStatus(ps.googleAds) },
      { name: 'Facebook', status: getStatus(ps.facebook) },
      { name: 'Reddit', status: getStatus(ps.reddit) },
      { name: 'TTD', status: getStatus(ps.ttd) },
      { name: 'DV360', status: getStatus(ps.dv360) },
      { name: 'Madhive', status: getStatus(ps.madhive) }
    ];
    
    return platforms;
  };

  const columns = [
    {
      key: 'name',
      label: 'Name',
      width: '1fr',
      sortable: true,
      resizable: true,
      render: (job) => (
        <div className="campaign-name-content">
          {job.isPinned && <FontAwesomeIcon icon={faThumbtack} className="pinned-icon" title="Pinned job" />}
          <span className="campaign-name-text" title={job.name}>{job.name}</span>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      sortable: true,
      render: (job) => <StatusBadge status={job.status} />
    },
    {
      key: 'platforms',
      label: 'Platforms',
      width: '280px',
      render: (job) => {
        const platforms = getPlatformSummary(job);
        if (platforms.length === 0) return <span className="text-muted">No platforms</span>;
        return (
          <div className="platform-badges">
            {platforms.map((p, i) => (
              <span key={i} className={`platform-badge ${p.status}`} title={`${p.name}: ${p.status}`}>
                {p.name}
              </span>
            ))}
          </div>
        );
      }
    },
    {
      key: 'progress',
      label: 'Progress',
      width: '160px',
      render: (job) => {
        const total = job.totalCampaigns || 0;
        const success = job.successfulCampaigns || 0;
        const failed = job.failedCampaigns || 0;

        return (
          <div className="progress-cell">
            <span className="progress-text">
              {success}/{total}
              {failed > 0 && <span className="failed-count" style={{ color: '#DC2626', marginLeft: '4px' }}>({failed} failed)</span>}
            </span>
          </div>
        );
      }
    },
    {
      key: 'folder_name',
      label: 'Folder',
      width: '180px',
      sortable: true,
      render: (job) => (
        <span className="folder-info">
          <FontAwesomeIcon icon={faFolder} />
          {job.folderName || 'No folder'}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      width: '180px',
      sortable: true,
      render: (job) => formatDate(job)
    },
    createActionColumn(null, dropdownRefs, openDropdownId, toggleDropdown)
  ];


  // Dropdown Menu Component
  const DropdownMenu = ({ job, onClose }) => {
    if (!job) return null;

    const handlePin = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        const response = await api.patch(getApiUrl(`/api/v2/creative-manager/${job.id}`), {
          is_pinned: !job.isPinned
        }, { params: { accountId, userId: user?.userId || user?.id } });

        if (response.status === 200) {
          const updatedJobs = jobs.map(j => j.id === job.id ? { ...j, isPinned: !j.isPinned } : j);
          updatedJobs.sort((a, b) => {
            if (a.isPinned && !b.isPinned) return -1;
            if (!a.isPinned && b.isPinned) return 1;
            return 0;
          });
          setJobs(updatedJobs);
        }
      } catch (err) {
        console.error('Error updating pin status:', err);
        setError('Failed to update job. Please try again.');
      }
      onClose();
    };

    const handleDelete = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (window.confirm(`Are you sure you want to delete "${job.name}"?`)) {
        try {
          await api.delete(getApiUrl(`/api/v2/creative-manager/${job.id}`), {
            params: { accountId, userId: user?.userId || user?.id }
          });
          setJobs(prev => prev.filter(j => j.id !== job.id));
          setPagination(prev => ({ ...prev, totalItems: Math.max(0, prev.totalItems - 1) }));
        } catch (err) {
          console.error('Error deleting job:', err);
          setError('Failed to delete job. Please try again.');
        }
      }
      onClose();
    };

    return (
      <div className="sidebar-dropdown-menu" style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }} onClick={(e) => e.stopPropagation()}>
        <button onMouseDown={handlePin} className="sidebar-dropdown-item">
          <FontAwesomeIcon icon={faThumbtack} />
          {job.isPinned ? 'Unpin' : 'Pin'}
        </button>
        <button onMouseDown={handleDelete} className="sidebar-dropdown-item delete">
          <FontAwesomeIcon icon={faTrash} />
          Delete
        </button>
      </div>
    );
  };

  if (!accountId) {
    return (
      <div className="page-container">
        <div className="page-error">
          <p>Please select an account to view creative manager jobs.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-left">
          {selectedFolder && (
            <div className="breadcrumb-nav">
              <button onClick={() => handleFolderSelect(null)} className="breadcrumb-back-btn">
                <FontAwesomeIcon icon={faArrowLeft} />
                All Folders
              </button>
              <span className="breadcrumb-separator">›</span>
              <span className="breadcrumb-current">{getFolderDisplayLabel(selectedFolder)}</span>
            </div>
          )}
          <h1>
            Creative Manager
            {selectedFolder && <span className="folder-context">in {getFolderDisplayLabel(selectedFolder)}</span>}
            <span className="count-badge">{pagination.totalItems || jobs.length}</span>
          </h1>
          <p>
            {selectedFolder
              ? `Creative upload jobs in the "${getFolderDisplayLabel(selectedFolder)}" folder`
              : 'Manage your creative uploads across platforms'
            }
          </p>
        </div>
        <div className="header-right">
          {/* Folder Filter */}
          <div className="folder-filter-container">
            <button type="button" className={`folder-filter-button ${showFolderDropdown ? 'active' : ''}`} onClick={() => setShowFolderDropdown(!showFolderDropdown)} disabled={foldersLoading}>
              <FontAwesomeIcon icon={faFolder} />
              <span>{selectedFolder ? getFolderDisplayLabel(selectedFolder) : 'All Folders'}</span>
              <FontAwesomeIcon icon={showFolderDropdown ? faChevronUp : faChevronDown} className="dropdown-arrow" />
            </button>
            {showFolderDropdown && (
              <div className="folder-filter-dropdown">
                <button type="button" className={`folder-filter-option ${!selectedFolder ? 'selected' : ''}`} onClick={() => handleFolderSelect(null)}>
                  <FontAwesomeIcon icon={faFolder} />
                  <span>All Folders</span>
                </button>
                <div className="folder-filter-divider" />
                {folders.map(folder => (
                  <button key={folder.id} type="button" className={`folder-filter-option ${selectedFolder?.id === folder.id ? 'selected' : ''}`} onClick={() => handleFolderSelect(folder)}>
                    <FontAwesomeIcon icon={faFolder} />
                    <span>{getFolderDisplayLabel(folder)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="search-box">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input type="text" placeholder="Search jobs..." value={searchTerm} onChange={handleSearchChange} className="search-input" />
          </div>

          {!uploadCreativesHidden && (
            <button className="btn btn-primary" onClick={() => navigate('/creative-manager/new')}>
              <FontAwesomeIcon icon={faPlus} />
              Upload Creatives
            </button>
          )}
        </div>
      </div>

      {error && <div className="page-error"><p>{error}</p></div>}

      <DataTable
        data={jobs}
        columns={columns}
        pagination={pagination}
        onSort={requestSort}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        onRowClick={(job, e) => {
          if (e.target.closest('.dropdown-container') || e.target.closest('.sidebar-action-button')) return;
          navigate(`/creative-manager/${job.id}`);
        }}
        sortBy={sortBy}
        sortOrder={sortOrder}
        isLoading={isLoading}
        emptyMessage={
          <div className="empty-state">
            <FontAwesomeIcon icon={faImages} className="empty-state-icon" />
            <h3>No creative jobs yet</h3>
            {!uploadCreativesHidden && (
              <p>Upload your first batch of creatives to get started</p>
            )}
          </div>
        }
        className="creative-manager-table"
        storageKey="creative-manager"
      />

      {openDropdownId && <DropdownMenu job={jobs.find(j => j.id === openDropdownId)} onClose={() => setOpenDropdownId(null)} />}
    </div>
  );
};

export default CreativeManagerPage;

